import axios from "axios";
import { API } from "../utils/apiUrls";

function safeFilenameSegment(ref) {
  return String(ref)
    .replace(/[/\\?%*:|"<>]/g, "-")
    .trim()
    .slice(0, 120);
}

function pickDownloadUrl(payload) {
  if (!payload || typeof payload !== "object") return null;
  const direct =
    payload.shipment_invoice_presigned_url ||
    payload.invoice_url ||
    payload.download_url ||
    payload.file_url ||
    payload.url ||
    payload.presigned_url ||
    payload.label_csv_file_url;
  if (direct) return direct;
  const first = Array.isArray(payload.invoices) ? payload.invoices[0] : null;
  if (first && typeof first === "object") {
    return first.url || first.download_url || first.invoice_url || null;
  }
  return null;
}

async function blobToJson(blob) {
  const text = await blob.text();
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function extensionFromContentType(ctype) {
  const c = (ctype || "").toLowerCase();
  if (c.includes("pdf")) return "pdf";
  if (c.includes("zip")) return "zip";
  if (c.includes("csv")) return "csv";
  return "bin";
}

function saveBlob(blob, filename) {
  const objectUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function parseErrorFromBlobResponse(err) {
  const data = err?.response?.data;
  if (!(data instanceof Blob)) {
    return err?.response?.data?.message || err?.message || "Invoice download failed";
  }
  const text = await data.text();
  try {
    const json = JSON.parse(text);
    if (json && typeof json === "object") {
      return json.message || json.error || json.detail || "Invoice download failed";
    }
  } catch {
  }
  return text?.slice(0, 500) || err.message || "Invoice download failed";
}

async function tryJsonPayload(blob) {
  const json = await blobToJson(blob);
  if (!json || typeof json !== "object") return null;
  const fileUrl = pickDownloadUrl(json);
  if (fileUrl) {
    window.open(fileUrl, "_blank", "noopener,noreferrer");
    return true;
  }
  if (json.message && (json.success === false || json.error)) {
    throw new Error(json.message);
  }
  return null;
}

export async function downloadShipmentInvoice({
  merchantId,
  orderReferenceNumber,
  authToken,
  signal,
}) {
  const ref = String(orderReferenceNumber || "").trim();
  if (!ref) throw new Error("Order reference is required");
  if (!merchantId) throw new Error("Merchant ID is required");
  if (!authToken) throw new Error("Not authenticated");

  const url = API.shipmentInvoice();
  const body = {
    merchant_id: String(merchantId),
    order_reference_number: ref,
  };
  const headers = {
    Authorization: authToken,
    "Content-Type": "application/json",
    "x-merchant-id": String(merchantId),
  };

  let res;
  try {
    res = await axios.put(url, body, {
      headers,
      responseType: "blob",
      signal,
    });
  } catch (err) {
    const message = await parseErrorFromBlobResponse(err);
    throw new Error(message);
  }

  const rawCtype = String(res.headers["content-type"] || "")
    .split(";")[0]
    .trim()
    .toLowerCase();
  const blob = res.data;

  if (rawCtype.includes("application/json") || rawCtype.endsWith("+json")) {
    const opened = await tryJsonPayload(blob);
    if (opened) return { mode: "url" };
    throw new Error("Invoice response did not include a download URL");
  }

  if (blob instanceof Blob && blob.size < 65536) {
    const opened = await tryJsonPayload(blob);
    if (opened) return { mode: "url" };
  }

  const safeRef = safeFilenameSegment(ref);
  const ext = extensionFromContentType(rawCtype);
  const isPdf =
    ext === "pdf" ||
    rawCtype.includes("pdf") ||
    (blob instanceof Blob && String(blob.type || "").toLowerCase().includes("pdf"));

  if (isPdf && blob instanceof Blob) {
    const objectUrl = URL.createObjectURL(blob);
    window.open(objectUrl, "_blank", "noopener,noreferrer");
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 120_000);
    return { mode: "tab", filename: `invoice-${safeRef || "order"}.pdf` };
  }

  const filename = `invoice-${safeRef || "order"}.${ext}`;
  saveBlob(blob, filename);
  return { mode: "blob", filename };
}
